import React, { useContext, useEffect, useState, useRef } from 'react';
import styled, { css } from 'styled-components';
import { Interaction, LivestreamOverlays } from 'components';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { AnimatePresence, motion } from 'framer-motion';
import { fullGridContainer, breakpoints } from 'styles';
import Vimeo from '@vimeo/player';
import { useIntersection, useWindowSize } from 'hooks';
import { scrollToAnchor } from 'utils';
import throttle from 'just-throttle';
import { LocalContext, FirebaseContext } from 'context';

const LivestreamContainer = ({ colors }) => {
  const { isMobile, theme, selectedEvent } = useContext(LocalContext);
  const { eid } = selectedEvent;
  const [isPipModeEnabled, setIsPipModeEnabled] = useState(false);
  const { user, firebase, loading } = useContext(FirebaseContext);
  const [vimeoPlayer, setVimeoPlayer] = useState(null);
  const [livestreamIsPlaying, setLivestreamIsPlaying] = useState(false);
  const [questionCurrentlyBeingAnsweredLive, setQuestionCurrentlyBeingAnsweredLive] =
    useState(null);
  const [pipRef, vimeoPlayerObserver] = useIntersection({
    initialInView: true,
    threshold: 0.75
  });
  const [forcedActiveTab, setForcedActiveTab] = useState(null);

  const livestreamRef = useRef(null);

  useEffect(() => {
    if (user && firebase?.auth?.currentUser && livestreamRef) {
      scrollToAnchor('livestream', -88, 1400)
    }
  }, [user, firebase, livestreamRef]);

  useEffect(() => {
    let unsubscribeFromEventUpdates;

    if (firebase) {
      unsubscribeFromEventUpdates = firebase.subscribeToEventUpdates({
        eid,
        onSnapshot: (snapshot) => {
          if (snapshot.exists) {
            const {
              forcedActiveTab: _forcedActiveTab,
              questionCurrentlyBeingAnsweredLive: _questionCurrentlyBeingAnsweredLive
            } = snapshot.data();

            if (_forcedActiveTab || _forcedActiveTab === null) {
              setForcedActiveTab(_forcedActiveTab);
            }

            if (_questionCurrentlyBeingAnsweredLive) {
              setQuestionCurrentlyBeingAnsweredLive(_questionCurrentlyBeingAnsweredLive);
            } else {
              setQuestionCurrentlyBeingAnsweredLive(null);
            }
          } else if (!snapshot.exists) {
            /* CONOR TODO: Is there a better way of doing this? Can we automatically add the event doc somehow? Hmmmm */
            console.error(
              "You need to add a doc for this event in the 'Events' collection in Firebase."
            );
          }
        }
      });
      return () => {
        if (unsubscribeFromEventUpdates) {
          unsubscribeFromEventUpdates();
        }
      };
    }
  }, [firebase]);

  useEffect(() => {
    const livestreamIframe = document.getElementById('livestream-iframe');
    let updateVideoSessionDataInterval;

    if (livestreamIframe && user && !vimeoPlayer) {
      const _vimeoPlayer = new Vimeo(livestreamIframe);

      setVimeoPlayer(_vimeoPlayer);

      // I'm defining a video "session" as a discrete period of time someone is on the page watching
      // a video. If someone watches an hour one day, then navigates away and comes back to the same
      // page the next day, then those are two seperate sessions. Likewise, if they watch 20 mins
      // then navigate to another part of the site and then come back to the same page again and
      // watches another 20 mins, then those are two seperate sessions. But if someone watches 20
      // mins and pauses the video, then unpauses and continues watching, then that's a single
      // session. Likewise, if someone watches 20 mins and then fast-forwards an hour and continues
      // watching, then that's also a single session.
      const updateVideoSessionData = async () => {
        const data = await _vimeoPlayer.getPlayed();

        const timeRange = data.map((arr) => {
          return {
            start: parseFloat(arr[0].toFixed(2)),
            end: parseFloat(arr[1].toFixed(2))
          };
        });

        return firebase.updateVideoSessionData({
          uid: user.uid,
          eid,
          timeRange
        });
      };

      updateVideoSessionDataInterval = () => throttle(updateVideoSessionData, 60000);

      _vimeoPlayer.on('timeupdate', updateVideoSessionDataInterval());
      _vimeoPlayer.on('play', (data) => {
        // Using an 'if' statement here because when the 'play' event is fired
        // after any 'seek' event 'data' is undefined.
        if (data) {
          setLivestreamIsPlaying(true);
        }
      });
      _vimeoPlayer.on('pause', (data) => {
        setLivestreamIsPlaying(false);

        // The 'pause' event is also fired when the video ends, along with the 'ended' event.
        // We want to ignore it when the video has ended, so we'll check the percent value.
        if (data.percent !== 1) {
          updateVideoSessionData();
        }
      });
      _vimeoPlayer.on('seeked', updateVideoSessionData);
      _vimeoPlayer.on('ended', updateVideoSessionData);

      _vimeoPlayer.on('enterpictureinpicture', () => {
        setIsPipModeEnabled(true);
      });
      _vimeoPlayer.on('leavepictureinpicture', () => {
        setIsPipModeEnabled(false);
      });
    }

    return () => {
      // document.removeEventListener('keydown', keyDownListener);
      clearInterval(updateVideoSessionDataInterval);
      vimeoPlayer?.off('timeupdate');
      vimeoPlayer?.off('play');
      vimeoPlayer?.off('pause');
      vimeoPlayer?.off('seeked');
      vimeoPlayer?.off('ended');
      vimeoPlayer?.off('enterpictureinpicture');
      vimeoPlayer?.off('leavepictureinpicture');
    };
  }, [user, vimeoPlayer]);

  useEffect(() => {
    if (
      document.pictureInPictureEnabled &&
      vimeoPlayer &&
      !vimeoPlayer.disablePictureInPicture &&
      livestreamIsPlaying &&
      !vimeoPlayerObserver.intersecting
    ) {
      try {
        vimeoPlayer.requestPictureInPicture();
      } catch (err) {
        console.error(err);
      }
    }
  }, [vimeoPlayer, vimeoPlayerObserver]);

  const handlePipOverlayClick = () => vimeoPlayer.exitPictureInPicture();

  return (
    <LivestreamAndInteraction>
      <LiveStream ref={livestreamRef} id="livestream">
        <div
          ref={pipRef}
          style={{
            padding: '56.25% 0 0 0',
            height: '100%',
            position: 'relative',
            overflow: 'hidden'
          }}>
          <iframe
            title="Retina 2021"
            id="livestream-iframe"
            src="https://player.vimeo.com/video/641846305?h=4fd6d763ce&autoplay=1&fullscreen=1&controls=1"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%'
            }}
          />
          <LivestreamOverlays
            colors={colors}
            eid={eid}
            handlePipOverlayClick={handlePipOverlayClick}
            isPipModeEnabled={isPipModeEnabled}
            livestreamRef={livestreamRef}
            questionCurrentlyBeingAnsweredLive={questionCurrentlyBeingAnsweredLive}
          />
        </div>
      </LiveStream>
      <Interaction
        colors={colors}
        eid={eid}
        forcedActiveTab={forcedActiveTab}
        questionCurrentlyBeingAnsweredLive={questionCurrentlyBeingAnsweredLive}
      />
    </LivestreamAndInteraction>
  );
};

const LivestreamAndInteraction = styled.div`
  display: grid;
  grid-column: 1/7;
  grid-gap: 1rem;
  grid-template-columns: 1fr;
  padding: 1.5rem 1rem;
  width: 100%;
  @media (min-width: ${breakpoints.sm}) {
    grid-column: 2/6;
  }
  @media (min-width: ${breakpoints.lg}) {
    grid-column: 1/13;
    grid-template-columns: 2fr 1fr;
  }
`;

const LiveStream = styled.div`
  height: auto;
  margin: 1.25rem -1.25rem;
  position: relative;
  @media (min-width: 1150px) {
    margin: 0px;
    height: 100%;
  }
`;

export default LivestreamContainer;
